export const PROVIDER_LIMIT_MESSAGE =
  'Le fournisseur de prix a refusé la requête ou impose un délai. Les prix enregistrés sont conservés. Relancez l’actualisation manuellement plus tard.'

export function itemsPriceQueryKey(cartId, userId) {
  return userId ? ['items', cartId, userId] : ['items', cartId]
}

export function isValidScrapedPrice(price) {
  const value = Number(price)
  return Number.isFinite(value) && value > 0
}

export function canStartItemRefresh(refreshingIds, itemId) {
  if (!itemId) return false
  return !refreshingIds?.has(itemId)
}

export function isItemRefreshing(refreshingIds, itemId) {
  return Boolean(itemId && refreshingIds?.has(itemId))
}

export function formatLastPriceCheck(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function detailText(error) {
  const detail = error?.response?.data?.detail || error?.message || ''
  return typeof detail === 'string' ? detail : String(detail || '')
}

export function describePriceRefreshError(error) {
  const status = error?.response?.status ?? error?.status ?? null
  const detail = detailText(error)
  const text = detail.toLowerCase()
  const isLimit =
    status === 429 ||
    text.includes('429') ||
    text.includes('rate limit') ||
    text.includes('too many requests') ||
    text.includes('quota') ||
    text.includes('retry-after') ||
    text.includes('retry after')

  if (isLimit) {
    return {
      title: 'Limite du fournisseur',
      message: detail.trim() ? detail : PROVIDER_LIMIT_MESSAGE,
      preservePrice: true,
    }
  }

  if (status === 400) {
    return {
      title: 'Données invalides',
      message: detail.trim() || 'Les informations de prix sont invalides. Le prix enregistré est conservé.',
      preservePrice: true,
    }
  }

  if (status === 401 || status === 403) {
    return {
      title: 'Accès refusé',
      message: detail.trim() || 'Vous n’avez pas accès à cet article. Le prix enregistré est conservé.',
      preservePrice: true,
    }
  }

  if (status === 404) {
    return {
      title: 'Article inaccessible',
      message: detail.trim() || 'Article introuvable. Le prix enregistré est conservé.',
      preservePrice: true,
    }
  }

  if (status === 502 || status === 503) {
    return {
      title: 'Service distant indisponible',
      message: detail.trim() || 'Le service distant est indisponible. Le prix enregistré est conservé.',
      preservePrice: true,
    }
  }

  if (status === 500) {
    return {
      title: 'Erreur interne',
      message: detail.trim() || 'Erreur interne lors de l’actualisation du prix. Le prix enregistré est conservé.',
      preservePrice: true,
    }
  }

  return {
    title: 'Erreur',
    message: detail || 'Impossible d’actualiser le prix. Le prix enregistré est conservé.',
    preservePrice: true,
  }
}

export function nextRefreshingIds(current, itemId, action) {
  const next = new Set(current)
  if (action === 'add') next.add(itemId)
  if (action === 'remove') next.delete(itemId)
  return next
}

export function extensionSyncKey(payload) {
  if (!payload || typeof payload !== 'object') return ''
  const data = payload.data && typeof payload.data === 'object' ? payload.data : {}
  return JSON.stringify({
    type: payload.type || null,
    itemId: data.itemId ?? data.item_id ?? null,
    cartId: data.cartId ?? data.cart_id ?? null,
    price: data.price ?? null,
    stamp: data.updatedAt ?? data.timestamp ?? data.last_price_check ?? null,
  })
}

export function shouldHandleExtensionSync(lastKeyRef, payload) {
  const key = extensionSyncKey(payload)
  if (!key || lastKeyRef?.current === key) return false
  lastKeyRef.current = key
  return true
}

export function nextPriceRefreshUiState({
  oldPrice,
  newPrice,
  historyRecorded,
  historySkipped,
} = {}) {
  const from = Number(oldPrice)
  const to = Number(newPrice)
  const changed =
    Number.isFinite(from) &&
    Number.isFinite(to) &&
    Math.round(from * 100) !== Math.round(to * 100)
  const partialHistory = historyRecorded === false && historySkipped === false
  return {
    priceRoll: changed ? { from, to } : null,
    warning: partialHistory
      ? 'Prix actualisé, mais l’historique n’a pas pu être enregistré.'
      : null,
  }
}
